import { useEffect, useRef } from 'react';
import lottie from 'lottie-web/build/player/lottie_light';
import animationData from '../assets/deskey-loader.json';

// Mounts the sparkle Lottie into `boxRef` for the component's lifetime.
function useSparkleAnimation(boxRef) {
  useEffect(() => {
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const anim = lottie.loadAnimation({
      container: boxRef.current, renderer: 'svg', loop: true, autoplay: !reduceMotion, animationData,
    });
    return () => anim.destroy();
  }, [boxRef]);
}

/**
 * Branded sparkle animation — same Lottie file as the teacher mobile app's
 * Loader (teacher-mobile/assets/deskey-loader.json). Use it while AI is
 * working (generating, scoring, thinking). Pages and blocks waiting on plain
 * data use skeletons instead (student side: pages/student/StudentSkeletons.jsx).
 * `fullscreen` fills the viewport instead of sitting inline.
 */
export default function Loader({ label, fullscreen = false, size, className = '' }) {
  const boxRef = useRef(null);
  const dim = size ?? (fullscreen ? 160 : 96);
  useSparkleAnimation(boxRef);
  return (
    <div role="progressbar" aria-label={label ?? 'Loading'} data-testid="loader"
      className={`flex flex-col items-center justify-center ${fullscreen ? 'min-h-screen bg-white' : 'py-8'} ${className}`}>
      <div ref={boxRef} style={{ width: dim, height: dim }} aria-hidden="true" />
      {label ? <p className="mt-1 text-sm text-zinc-500">{label}</p> : null}
    </div>
  );
}

/** Small sparkle that sits inside a button or chat bubble while AI works. */
export function InlineSparkle({ size = 16, label = 'Working', className = '' }) {
  const boxRef = useRef(null);
  useSparkleAnimation(boxRef);
  return (
    <span role="progressbar" aria-label={label} data-testid="inline-sparkle"
      className={`inline-block shrink-0 ${className}`} style={{ width: size, height: size }}>
      <span ref={boxRef} className="block w-full h-full" aria-hidden="true" />
    </span>
  );
}
